'use client';
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';

import type { AuthTokens, User } from '@nutralab/api';

import { browserApi } from '@/shared/api/browser';
import { hasAuthCookie, persistAuthTokens, readAuthTokens } from '@/shared/utils';

interface AuthContextValue {
    isAuthenticated: boolean;
    isInitializing: boolean;
    user: User | null;
    loginVersion: number;
    setSession: (tokens: AuthTokens) => Promise<void>;
    logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** The storefront session (`useAuth`): restores the saved tokens, loads the profile, signs in and out. */
export function AuthProvider({ children }: { children: React.ReactNode }): React.ReactElement {
    const [tokens, setTokens] = useState<AuthTokens | null>(null);
    const [user, setUser] = useState<User | null>(null);
    const router = useRouter();
    const [isInitializing, setIsInitializing] = useState(true);
    const [loginVersion, setLoginVersion] = useState(0);

    useEffect(() => {
        // Checked before reading: reading moves an older localStorage session into the cookie.
        const isCookieMissing = !hasAuthCookie();
        const stored = readAuthTokens();
        if (!stored) {
            setIsInitializing(false);
            return;
        }

        // Rehydrating a saved session: the transport refreshes an expired token on the way. Re-render the server
        // part when its cookie was missing or the token changed, so SSR sees the current one.

        browserApi.user
            .getProfile()
            .then(({ user: profile }) => {
                const current = readAuthTokens() ?? stored;
                persistAuthTokens(current);
                setTokens(current);
                setUser(profile);
                if (isCookieMissing || current.access_token !== stored.access_token) {
                    router.refresh();
                }
            })
            .catch(() => {
                persistAuthTokens(null);
            })
            .finally(() => {
                setIsInitializing(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const setSession = useCallback(
        async (next: AuthTokens) => {
            persistAuthTokens(next);
            try {
                const { user: profile } = await browserApi.user.getProfile();
                setTokens(next);
                setUser(profile);
                setLoginVersion((v) => v + 1);
                router.refresh();
            } catch (err) {
                persistAuthTokens(null);
                throw err;
            }
        },
        [router],
    );

    const logout = useCallback(async () => {
        if (tokens) {
            try {
                await browserApi.auth.logout();
            } catch {
                // best-effort — session is cleared locally regardless
            }
        }
        persistAuthTokens(null);
        setTokens(null);
        setUser(null);
        router.refresh();
    }, [tokens, router]);

    return (
        <AuthContext.Provider
            value={{
                isAuthenticated: Boolean(tokens),
                isInitializing,
                user,
                loginVersion,
                setSession,
                logout,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth(): AuthContextValue {
    const ctx = useContext(AuthContext);
    if (!ctx) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return ctx;
}
